import { Delete, DinnerDining, DirectionsBoat, DirectionsBus, Flight, Hotel, Info, LocationCity, Speaker } from '@mui/icons-material';

const venueReasoning = "The GHG emission sources attributable to the event venue include on-site energy consumption (including grid electricity consumption from lighting, air conditioning, audio/visual system, etc.), and water consumption. This calculator does not include embodied carbon of building materials unless incurred in space design and production (e.g., stands, booths, carpets and furniture).";
const mediaReasoning = "The GHG emission sources attributable to communication include paper and board materials and electronic media. Printed materials such as forms, papers, brochures, name tags and exhibition posters require different categories of paper and board with distinct texture, weights and thus carbon intensities. On the other hand, the GHG emissions from common electronic devices used for communication are generated at both physical and virtual events.";
const cateringReasoning = "The GHG emissions attributable to catering comes from food and beverages (F&B) served as well as tableware provided. Growing and processing raw food ingredients are responsible for considerable carbon emissions. On average, carbon emissions from chicken production are less than 1/8 of the emissions from the same amount of beef production.";
const wasteReasoning = "The GHG emission sources attributable to waste include metal, wood, paper/cardboard, plastics and food. Re-use. Recycling and disposal are three major ways to handle waste from MICE events. In Singapore, 99% of metal is recycled, compared to only 6% for plastics.";
const intlReasoning = "International transportation is typically responsible for dominant share of carbon footprint of an event with international participants. Air travel is the most popular mode of international travel globally for MICE events. The carbon emissions from civil aviation is highly correlated with the distance of travel, among other factors such as engine model of the aircraft.";
const localReasoning = "The GHG emissions from local transportation depends on the mode of travel. This calculator employs a large database of emission factors for each mode of travel, including both privately-owned vehicles and public transport. The distance of travel is measured from the location of local residence to the event venue.";
const freightReasoning = "To successfully set up a functional MICE event, materials and equipment external to the event organiser are often needed to be shipped to the event venue.Similar tolocal passenger transportation, freight transportation is contributing to MICE carbon footprint proportional to the distance of travel. With capacity of shipping vehicle limited for each trip, mass of freight shipped is another important factor in the carbon estimation.";
const hotelReasoning = "The GHG emissions attributable to accommodation depend on the number of hotel rooms occupied and the carbon intensity of these hotel rooms. The event organiser is responsible for carbon footprint of arranged accommodation, and the extra time of accommodation often goes beyond the scope of event carbon estimation.";

const foodOpts = [
    {
        value: "0-10"
    },
    {
        value: "10-50"
    },
    {
        value: "50-100"
    },
    {
        value: "100-150"
    },
    {
        value: "150-200"
    }
];

// tags are used to assign the correct page index for navigation
export const counterRelationships = {
    "vegetarianRs": {
        isAlwaysShown: false,
        tag: 'o-7',
        category: "onsite",
        group1: {
            tab: "Onsite Activities",
            label: ["Number of Vegetarians"],
            fields: [ "o2_num_vegetarians", "o2_num_non_vegetarians" ]
        },
        group2: {
            tab: "Global",
            label: ["Total number of Physical Attendees"],
            fields: ["g1_physical_attendees"],
        },
    },
    "frac_tripsRs": {
        dependentOn: 't1_intl_method:a',
        isAlwaysShown: true,
        tag: 't-1',
        category: "transport",
        group1: {
            tab: "Transportation",
            label: ["Percentage Breakdown of Flight Categories"],
            fields: [ "t1_fraction_economy_class-a", "t1_fraction_premium_class-a", "t1_fraction_private_jet-a" ],
        },
    },
    "travellerRs": {
        dependentOn: 't1_intl_method:a',
        isAlwaysShown: true, // To render counter only if a certain condtion is met (i.e. sums exceeded)
        tag: 't-2',
        category: "transport",
        group1: {
            tab: "Transportation",
            label: ["Sum of Travellers from different regions"],
            fields: [ 't1_travellers_southeast_asia-a', 't1_travellers_greater_china-a', 't1_travellers_north_asia-a', 't1_travellers_south_asia-a', 't1_travellers_west_asia-a', 't1_travellers_americas-a', 't1_travellers_europe-a', 't1_travellers_oceania-a', 't1_travellers_africa-a', 't1_travellers_others-a' ]
        },
        group2: {
            tab: "Global",
            label: ["Total number of International Attendees"],
            fields: ["g1_international_attendees"],
        },
    },
    "frac_roomOccupancyRs": {
        dependentOn: 'h1_room_method:b',
        isAlwaysShown: true,
        tag: 'h-1',
        category: "accommodations",
        group1: {
            tab: "Accommodations",
            label: ["Percentage Breakdown of Room Occupancy"],
            fields: [ "h1_frac_single_room-b", "h1_frac_double_room-b" ],
        },
    },
}

export const FormStructure = {
    /*
    ========================== STRUCTURE REFERENCE ==========================
    category : {
        tabTitle,
        tabDescription,
        sections: [
            {
                title,
                tag,
                subsections: [
                    {
                        title,
                        fields: [
                             {
                                id_name,
                                label,
                                caption,
                                isText,
                                isAddress / isButtonSelect / isDatePicker / isRadio / isSelect / isToggleable,
                                maxValue,
                                default_val,
                                options: [
                                    {
                                        label,
                                        value
                                    }
                                ],
                            },
                        ]
                    },
                ]
            },
        ]
    },
    */
    "global" : {
        tabTitle: "General Information",
        tabDescription: "",
        sections: [
            {
                title: "Overview",
                icon: <Info />,
                tag: "g-1",
                subsections: [
                    {
                        fields: [
                            {
                                id_name: "g1_event_name",
                                label: "Event name",
                                caption: "",
                                description: "",
                                isRequired: true,
                                isText: true,
                            },
                            {
                                id_name: "g1_venue_name",
                                label: "Venue name",
                                caption: "",
                                description: "",
                                isRequired: true,
                                isText: true,
                                isAddress: true,
                            },
                            {
                                id_name: "g1_event_period",
                                label: "Event period",
                                caption: "Start and end date of event",
                                description: "",
                                isRequired: true,
                                isDatePicker: true,
                            },
                        ]
                    }
                ]
            },
            
            {
                title: "Overview",
                tag: "g-2",
                subsections: [
                    {
                        fields: [
                            {
                                id_name: "g1_hours_per_day",
                                label: "Average event duration per day",
                                caption: "The average number of hours the event runs each day.",
                                description: "Input the average daily event duration in hours per day. This number will be used as the effective duration the on-site hardware system is active each day. Longer event duration means higher emissions from electricity usage and on-site activities. A typical event may take 6 ~ 16 hours each day.",
                                units: "hours",
                                maxValue: 24,
                                isRequired: true,
                                options: [
                                    {
                                        value: "1"
                                    },
                                    {
                                        value: "2"
                                    },
                                    {
                                        value: "4"
                                    },
                                    {
                                        value: "8"
                                    },
                                    {
                                        value: "12"
                                    },
                                ],
                            },
                            {
                                id_name: "g1_setting",
                                label: "Event setting",
                                caption: "The MICE event setting. Note that different rooms have different capacities.",
                                description: "Select an event setting from the dropdown menu. A typical conference venue is designed with functions of exhibition, theatre, classroom, cluster and banquet. Depending on the event setting, the event rooms may have different capacities with different lighting, visual and audio systems, which affect the electricity usage that leads to different rates of carbon emissions.",
                                isRequired: true,
                                isSelect: true,
                                options: [
                                    {
                                        value: "Exhibition",
                                    },
                                    {
                                        value: "Theatre",
                                    },
                                    {
                                        value: "Classroom",
                                    },
                                    {
                                        value: "Cluster",
                                    },
                                    {
                                        value: "Banquet",
                                    },
                                ],
                            },
                            {
                                id_name: "g1_event_meals_provided",
                                label: "Are meals provided for the event?",
                                isRequired: true,
                                isRadio: true,
                                options: [
                                    {
                                        value: 1,
                                        label: "Yes"
                                    },
                                    {
                                        value: 0,
                                        label: "No"
                                    },
                                ],
                            },
                        ]
                    }
                ]
            },
            {
                title: "Overview",
                tag: "g-3",
                subsections: [
                    {
                        fields: [
                            {
                                id_name: "g1_physical_attendees",
                                label: "Total number of physical attendees",
                                caption: "The number of people attending the MICE event in person.",
                                description: "Input the number of people estimated to attend the event in person. This includes both local and international event participants. The number of physical attendees largely affects the total carbon footprint but not per capita footprint. The choice of event venue and on-site hardware and resources should also take into account the number of physical attendees.",
                                units: "pax",
                                isRequired: true,
                                options: [
                                    {
                                        value: "50-100"
                                    },
                                    {
                                        value: "100-300"
                                    },
                                    {
                                        value: "300-500"
                                    },
                                    {
                                        value: "500-1000"
                                    },
                                    {
                                        value: "1000-5000"
                                    },
                                ],
                            },
                            {
                                id_name: "g1_virtual_attendees",
                                label: "Total number of virtual attendees",
                                isToggleable: true, // Need to answer yes/no before showing actual question
                                prelabel: "My event has virtual attendees",
                                caption: "The number of people attending the MICE event virtually via video conference.",
                                description: "Input the number of people estimated to attend the event virtually. Simply input 0 if the event does not support virtual attendance. Footprint from transportation and accommodation does not apply to virtual participants as there is no incremental carbon emissions incurred from video conference except emissions from video conference device usage.",
                                units: "pax",
                                isRequired: true,
                                options: [
                                    {
                                        value: "50-100"
                                    },
                                    {
                                        value: "100-300"
                                    },
                                    {
                                        value: "300-500"
                                    },
                                    {
                                        value: "500-1000"
                                    },
                                    {
                                        value: "1000-5000"
                                    },
                                ],
                            },
                            {
                                id_name: "g1_international_attendees",
                                isToggleable: true,
                                prelabel: "My event has international attendees",
                                label: "Total number of international attendees",
                                caption: "The number of international attendees among physical attendees.",
                                description: "Input the number of international attendees among physical attendees at the event. International attendees incur additional carbon emissions from air travel and hotel accommodation, which are a significant component in total event carbon footprint. The number of international attendees should be no greater than the total number of physical attendees.",
                                units: "pax",
                                isRequired: true,
                                options: [
                                    {
                                        value: "50-100"
                                    },
                                    {
                                        value: "100-300"
                                    },
                                    {
                                        value: "300-500"
                                    },
                                    {
                                        value: "500-1000"
                                    },
                                    {
                                        value: "1000-5000"
                                    },
                                ],
                            },
                            {
                                id_name: "g1_attendance_period_is_throughout",
                                label: "Do all attendees attend throughout the event period?",
                                caption: "Select the most appropriate response.",
                                description: "If attendees stay only for one day during the event period, the average attendees per day will be divided by the number of event days.",
                                isRequired: true,
                                isRadio: true,
                                options: [
                                    { 
                                        value: 1,
                                        label: "Yes, they attend for the entire event duration",
                                    },
                                    {
                                        value: 0,
                                        label: "No, they only attend for one day during entire event period",
                                    },
                                ],
                            },
                        ]
                    }
                ]
            },
        ]
    },
    "onsite": {
        tabTitle: "On-Site Activities",
        tabDescription: "",
        sections: [
            {
                title: "Venue",
                icon: <LocationCity />,
                reasoning: venueReasoning,
                tag: "o-1",
                subsections: [
                    {
                        title: "Room Sizes",
                        fields: [
                            {
                                id_name: "o3_room1_size",
                                label: "Size of venue",
                                caption: "Estimated floor area of the space used for the event.",
                                description: "",
                                units: "of floor space",
                                isSelect: true,
                                options: [
                                    {
                                        value: 'S1',
                                        label: "Up to 70 m\u00b2"
                                    },
                                    {
                                        value: 'S2',
                                        label: "Up to 125 m\u00b2"
                                    },
                                    {
                                        value: 'S3',
                                        label: "Up to 170 m\u00b2"
                                    },
                                    {
                                        value: 'M1',
                                        label: "Up to 220 m\u00b2"
                                    },
                                    {
                                        value: 'M2',
                                        label: "Up to 500 m\u00b2"
                                    },
                                    {
                                        value: 'L1',
                                        label: "Up to 800 m\u00b2"
                                    },
                                    {
                                        value: 'L2',
                                        label: "Up to 1000 m\u00b2"
                                    },
                                    {
                                        value: 'L3',
                                        label: "Up to 1500 m\u00b2"
                                    },
                                    {
                                        value: 'XL1',
                                        label: "Up to 2000 m\u00b2"
                                    },
                                    {
                                        value: 'XL2',
                                        label: "Up to 4000 m\u00b2"
                                    },
                                    {
                                        value: 'XL3',
                                        label: "Up to 12000 m\u00b2 or larger"
                                    },
                                ],
                            },
                            {
                                id_name: "o3_room2_size",
                                isToggleable: true, // Need to answer yes/no before showing actual question
                                prelabel: "My event has a secondary venue",
                                label: "Size of secondary venue/room",
                                caption: "Estimated floor area of any secondary space used for the event.",
                                description: "",
                                units: "of floor space",
                                isSelect: true,
                                options: [
                                    {
                                        value: 'S1',
                                        label: "Up to 70 m\u00b2"
                                    },
                                    {
                                        value: 'S2',
                                        label: "Up to 125 m\u00b2"
                                    },
                                    {
                                        value: 'S3',
                                        label: "Up to 170 m\u00b2"
                                    },
                                    {
                                        value: 'M1',
                                        label: "Up to 220 m\u00b2"
                                    },
                                    {
                                        value: 'M2',
                                        label: "Up to 500 m\u00b2"
                                    },
                                    {
                                        value: 'L1',
                                        label: "Up to 800 m\u00b2"
                                    },
                                    {
                                        value: 'L2',
                                        label: "Up to 1000 m\u00b2"
                                    },
                                    {
                                        value: 'L3',
                                        label: "Up to 1500 m\u00b2"
                                    },
                                    {
                                        value: 'XL1',
                                        label: "Up to 2000 m\u00b2"
                                    },
                                    {
                                        value: 'XL2',
                                        label: "Up to 4000 m\u00b2"
                                    },
                                    {
                                        value: 'XL3',
                                        label: "Up to 12000 m\u00b2 or larger"
                                    },
                                ],
                            },
                        ]
                    },
                    {
                        title: "Water",
                        fields: [
                            {
                                id_name: "o3_water_method",
                                label: "How would you prefer to estimate water consumption emissions?",
                                default_val : 'a',
                                isButtonSelect: true,
                                options: [
                                    {
                                        value: 'a',
                                        label: "Provide water consumption per attendee per day",
                                    },
                                    {
                                        value: 'b',
                                        label: "Provide total water consumption",
                                    }
                                ]
                            },
                            {
                                dependentOn: 'o3_water_method:a',
                                id_name: "o3_water_consumption_per_attendee-a",
                                label: "Water consumption per attendee per day",
                                caption: "Average water usage among physical attendees.",
                                units: "litres",
                            },
                            {
                                dependentOn: 'o3_water_method:b',
                                id_name: "o3_total_water_consumption-b",
                                label: "Total water consumption during event",
                                caption: "Estimated based on the number of attendees and national daily water usage.",
                                units: "litres",
                            },
                        ]
                    }
                ]
            },
            {
                title: "Venue",
                reasoning: venueReasoning,
                tag: "o-2",
                subsections: [
                    {
                        title: "Desktop Computers",
                        fields: [
                            {
                                id_name: "o3_num_desktops",
                                label: "Total number of desktops in use",
                                description: "Input the total number of desktop computers in use. Each desktop computer is assumed to handle a power of 200 watts.",
                                units: "desktops",
                            },
                        ]
                    },
                    {
                        title: "Audio",
                        fields: [
                            {
                                id_name: "o3_num_speakers",
                                label: "Total number of speakers in use",
                                description: "Input the total number of speakers in use. Each speaker is assumed to handle a power of 1600 watts.",
                                units: "speakers",
                            },
                        ]
                    },
                    {
                        title: "Visual",
                        fields: [
                            {
                                id_name: "o3_num_projectors",
                                label: "Total number of projectors in use",
                                description: "Input the total number of projectors in use. Each projector is assumed to handle a power of 360 watts.",
                                units: "projectors",
                            },
                        ]
                    },
                    {
                        title: "Heating, Ventilation and Air Conditioning (HVAC)",
                        fields: [
                            {
                                id_name: "o3_btu_sqm_h",
                                label: "BTUs per sqm per hour",
                                description: "Input the British thermal units (BTUs) per sqm per hour of the HVAC system. BTU is the amount of heat required to raise the temperature of one pound of water by one degree Fahrenheit.",
                            },
                        ]
                    },
                ]
            },
            {
                title: "Media",
                icon: <Speaker />,
                reasoning: mediaReasoning,
                tag: "o-3",
                subsections: [
                    {
                        title: "Paper and Board ( 1 / 3 )",
                        fields: [
                            {
                                id_name: "o1_forms_per_attendee",
                                label: "Forms per attendee per day",
                                caption: "The number of forms distributed to each physical attendee.",
                                description: "Input the number of forms distributed to each physical attendee. All forms are assumed to be printed on A4-sized uncoated woodfree (UCWF) office paper. These forms are typically used for event programme agenda, checklists, questionnaires and personal information collection, etc. Each form is assumed to be one A4-sized sheet.",
                                units: "pieces",
                            },
                            {
                                id_name: "o1_cp_per_attendee",
                                label: "Commercial prints per attendee per day",
                                caption: "The number of commercial papers distributed to each physical attendee.",
                                description: "Input the number of commercial papers distributed to each physical attendee. All commercial papers are assumed to be printed on A4-sized coated woodfree (CWF) typically used for magazines, books and commercial printing. These commercial papers are usually used for event programme agenda and advertising. Each copy is assumed to be two A4-sized sheets.",
                                units: "pieces",     
                            },
                            {
                                id_name: "o1_brochures_per_attendee",
                                label: "Brochures per attendee per day",
                                caption: "The number of brochures distributed to each physical attendee.",
                                description: "Input the number of brochures distributed to each physical attendee. All brochures are assumed to be printed on A4-sized coated groundwood (CGW) typically used for magazines, brochures and business cards. Each copy of the brochure is assumed to be 15 A4-sized sheets (30~60 pages).",
                                units: "pieces",     
                            },
                        ]
                    }
                ]
            },
            {
                title: "Media",
                reasoning: mediaReasoning,
                tag: "o-4",
                subsections: [
                    {
                        title: "Paper and Board ( 2 / 3 )",
                        fields: [
                            {
                                id_name: "o1_tissues_per_attendee",
                                label: "Tissues per attendee per day",
                                caption: "The number of tissues needed for each physical attendee.",
                                description: "Input the average number of tissues used by each physical attendee throughout the event period. These tissues may be used at meals or coffee/tea breaks.",
                                units: "pieces",     
                            },
                            {
                                id_name: "o1_tags_per_attendee",
                                label: "Name tags and lanyards per attendee per day",
                                caption: "The number of name tags distributed to each physical attendee.",
                                description: "Input the number of name tag and lanyard sets distributed to each physical attendee. Both name tags and lanyards are assumed to be made of polyester thread (PET) typically used for printed fabric products.",
                                units: "pieces",     
                            },
                            {
                                id_name: "o1_posters",
                                label: "Exhibition posters",
                                caption: "The number of posters displayed in the event.",
                                description: "Input the total number of posters used in the event. All posters are assumed to be printed on A0-sized bleached paperboard (BPB) typically used for posters and cardboards.",
                                units: "pieces",     
                            },
                        ]
                    }
                ]
            },
            {
                title: "Media",
                reasoning: mediaReasoning,
                tag: "o-5",
                subsections: [
                    {
                        title: "Paper and Board ( 3 / 3 )",
                        fields: [
                            {
                                id_name: "o1_cartons",
                                label: "Carton boxes",
                                caption: "The number of carton boxes used for the event.",
                                description: "Input the total number of carton boxes used in the event. All boxes are assumed to be made of unbleached paperboard (UBPB) typically used for cartons and boxes. ",
                                units: "pieces",     
                            },
                            {
                                id_name: "o1_banners",
                                label: "Backdrop banners",
                                caption: "The number of banners displayed in the event.",                           
                                description: "Input the total number of backdrop banners used in the event. All banners are assumed to be made of vinyl (PVC) typically used for banners and coating of cardboards. Each banner is assumed to be 304.8cm x 234.84cm.",
                                units: "pieces",     
                            },
                        ]
                    },
                    {
                        title: "Electronic Media",
                        fields: [
                            {
                                id_name: "o1_laptop_rate_physical",
                                label: "Laptop usage rate",
                                caption: "Average percentage of laptop usage among physical participants.",
                                description: "Input the average percentage of laptop usage among physical attendees. Virtual attendees are assumed to have a laptop usage rate of 100%. Each laptop is assumed to handle a power of 65 watts.",
                                step: 5,
                                units: "%",
                                maxValue: 100
                            },
                        ]
                    }
                ]
            },
            {
                title: "Catering",
                icon: <DinnerDining />,
                reasoning: cateringReasoning,
                tag: "o-6",
                subsections: [
                    {
                        title: "Catering ( 1 / 3 )",
                        fields: [
                            {
                                id_name: "o2_meals_per_day",
                                label: "Meals per day",
                                caption: "Including breakfast/lunch/dinner.",
                                description: "Input the number of meals (breakfast/lunch/dinner) per day. This number should be no greater than the total catering global input. A typical event organiser may arrange 1~3 catered meals per day.",
                                units: "meals",     
                            },
                            {
                                id_name: "o2_teas_per_day",
                                label: "Tea/Coffee breaks per day",
                                caption: "Including tea/coffee/snack breaks.",
                                description: "Input the number of tea/coffee breaks per day. This number and the number of meals should add up to the total catering global input. Light refreshments are often provided at tea/coffee breaks by event organisers.",
                                units: "breaks",     
                            },
                            {
                                id_name: "o2_cutlery",
                                label: "Cutlery choice",
                                caption: "Choose from polystyrene (PS), polypropylene (PP), polylactic acid (PLA), bamboo and stainless steel.",
                                description: "Choose from the dropdown menu the choice of cutlery. Tableware and cutlery are typically made of polystyrene (PS), polypropylene (PP), polylactic acid (PLA), PLA-PP, bamboo and steel. The disposal method also matters to the estimation of carbon footprint from tableware, with incineration and biodegradation as two major disposal methods.",
                                isSelect: true,
                                options: [
                                    {
                                        label: "Polystyrene",
                                        value: "PS"
                                    },
                                    {
                                        label: "Polypropylene",
                                        value: "PP"
                                    },
                                    {
                                        value: "PLA-PP (Incineration)",
                                    },
                                    {
                                        value: "PLA-PP (Biodegradation)",
                                    },
                                    {
                                        label: "Polylactic Acid (Incineration)",
                                        value: "PLA (Incineration)",
                                    },
                                    {
                                        label: "Polylactic Acid (Biodegradation)",
                                        value: "PLA (Biodegradation)",
                                    },
                                    {
                                        value: "Bamboo"
                                    },
                                    {
                                        value: "Stainless Steel (Used Once)"
                                    },
                                    {
                                        value: "Stainless Steel (After 29 Uses)"
                                    },
                                ]
                            },
                        ]
                    }
                ]
            },
            {
                title: "Catering",
                reasoning: cateringReasoning,
                tag: "o-7",
                subsections: [
                    {
                        title: "Catering ( 2 / 3 )",
                        fields: [
                            {
                                id_name: "o2_vegetarian_proportion",
                                label: "Meal Type Requirements for Physical Attendees",
                                caption: "Specify how many attendees at your event require plant-based meals versus those who will be served meat-based meals.",
                                description: "This will be used to derive the number of vegetarians among physical attendees. This number should be no greater than the total number of physical attendees global input. By default, 7% of the physical attendees are assumed to opt for the vegetarian option, taking no meat or seafood.",
                                units: "pax",
                                isSumLimit: true,
                                isInteger: true,
                                sumLimit: "global;g1_physical_attendees", // To be parsed in LimitSumField component
                                counterId: "vegetarianRs",
                                options: [
                                    {
                                        id_name: "o2_num_vegetarians",
                                        label: "Plant-based",
                                    },
                                    {
                                        id_name: "o2_num_non_vegetarians",
                                        label: "Meat-based",
                                    },
                                ],
                            },
                        ]
                    }
                ]
            },
            {
                title: "Catering",
                reasoning: cateringReasoning,
                tag: "o-8",
                subsections: [
                    {
                        title: "Catering ( 3 / 3 )",
                        fields: [
                            {
                                id_name: "o2_food_method",
                                label: "How would you prefer to estimate catering-related emissions?",
                                default_val : 'a',
                                isButtonSelect: true,
                                options: [
                                    {
                                        value: 'a',
                                        label: "Provide ingredient quantities per meal per person",
                                    },
                                    {
                                        value: 'b',
                                        label: "Provide total carbon footprint of food",
                                    }
                                ]
                            },
                            {
                                dependentOn: 'o2_food_method:a',
                                id_name: "o2_ingredient_per_meal-a",
                                label: "Weight of ingredient per meal per person",
                                units: "grams",
                                isGrouped: true,
                                groupedOptions: [
                                    {
                                        label: "Vegetarian Options",
                                        options: [
                                            {
                                                id_name: "o2_rice_per_meal-a",
                                                label: "Rice",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_wheat_per_meal-a",
                                                label: "Wheat",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_fruits_per_meal-a",
                                                label: "Fruits",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_leafy_per_meal-a",
                                                label: "Leafy Vegetables",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_other_veg_per_meal-a",
                                                label: "Other Vegetables",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_egg_per_meal-a",
                                                label: "Eggs",
                                                options: [ foodOpts ],
                                            },
                                        ]
                                    },
                                    {
                                        label: "Non-Vegetarian Options",
                                        options: [
                                            {
                                                id_name: "o2_beef_per_meal-a",
                                                label: "Beef",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_pork_per_meal-a",
                                                label: "Pork",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_chicken_per_meal-a",
                                                label: "Chicken",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_duck_per_meal-a",
                                                label: "Duck",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_mutton_per_meal-a",
                                                label: "Mutton",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_fish_per_meal-a",
                                                label: "Fish",
                                                options: [ foodOpts ],
                                            },
                                            {
                                                id_name: "o2_other_seafood_per_meal-a",
                                                label: "Other Seafood",
                                                options: [ foodOpts ],
                                            },
                                        ]
                                    },
                                ],
                            },
                            {
                                dependentOn: 'o2_food_method:b',
                                id_name: "o2_food_emission-b",
                                label: "Total Carbon Footprint of Food",
                                caption: "Total carbon footprint from food served at event in kg CO\u2082e. Not required if not readily available.",
                                description: "Input the estimated total carbon footprint of food in kg CO\u2082e. Some meal caterers provide this number for their sample meals. This carbon footprint excludes emissions related to tableware. The default value is provided based on the number of physical attendees, the number of vegetarians and the number of meals provided, considering Singapore adults’ daily consumption rates.",
                                units: "kg CO\u2082e",
                            },
                        ]
                    },
                ]
            },
            {
                title: "Waste",
                icon: <Delete />,
                reasoning: wasteReasoning,
                tag: "o-9",
                subsections: [
                    {
                        title: "Waste",
                        fields: [
                            {
                                id_name: "o4_waste_method",
                                label: "How would you prefer to estimate waste-related emissions?",
                                default_val : 'a',
                                isButtonSelect: true,
                                options: [
                                    {
                                        value: 'a',
                                        label: "Provide amount of each waste type",
                                    },
                                    {
                                        value: 'b',
                                        label: "Provide total amount of waste",
                                    }
                                ]
                            },
                            {
                                dependentOn: 'o4_waste_method:a',
                                id_name: "o4_wastetypes-a",
                                label: "Amount of each waste type",
                                isGrouped: true,
                                units: "kg",
                                options: [
                                    {
                                        id_name: "o4_metal_waste-a",
                                        label: "Metal waste",
                                        description: "Assumed to re-use 100%.",
                                        options: [
                                            {
                                                value: "0-500"
                                            },
                                            {
                                                value: "500-1000"
                                            },
                                            {
                                                value: "1000-1500"
                                            },
                                            {
                                                value: "1500-2000"
                                            },
                                        ],
                                    },
                                    {
                                        id_name: "o4_wood_waste-a",
                                        label: "Wood waste",
                                        description: "Assumed to re-use 100%.",
                                        options: [
                                            {
                                                value: "0-500"
                                            },
                                            {
                                                value: "500-1000"
                                            },
                                            {
                                                value: "1000-1500"
                                            },
                                            {
                                                value: "1500-2000"
                                            },
                                        ],
                                    },
                                    {
                                        id_name: "o4_papercardboard_waste-a",
                                        label: "Paper waste",
                                        description: "Estimated from paper and board.",
                                        options: [
                                            {
                                                value: "0-500"
                                            },
                                            {
                                                value: "500-1000"
                                            },
                                            {
                                                value: "1000-1500"
                                            },
                                            {
                                                value: "1500-2000"
                                            },
                                        ],
                                    },
                                    {
                                        id_name: "o4_plastic_waste-a",
                                        label: "Plastic waste",
                                        description: "Estimated based on plastic usage per person.",
                                        options: [
                                            {
                                                value: "0-500"
                                            },
                                            {
                                                value: "500-1000"
                                            },
                                            {
                                                value: "1000-1500"
                                            },
                                            {
                                                value: "1500-2000"
                                            },
                                        ],
                                    },
                                    {
                                        id_name: "o4_food_waste-a",
                                        label: "Food waste",
                                        description: "Estimated from food & beverages catering.",
                                        options: [
                                            {
                                                value: "0-500"
                                            },
                                            {
                                                value: "500-1000"
                                            },
                                            {
                                                value: "1000-2000"
                                            },
                                            {
                                                value: "2000-3000"
                                            },
                                            {
                                                value: "3000-4000"
                                            },
                                            {
                                                value: "4000-5000"
                                            },
                                        ],
                                    },
                                    {
                                        id_name: "o4_other_waste-a",
                                        label: "Others",
                                        description: "Assumed to re-use 100%.",
                                        options: [
                                            {
                                                value: "0-500"
                                            },
                                            {
                                                value: "500-1000"
                                            },
                                            {
                                                value: "1000-1500"
                                            },
                                            {
                                                value: "1500-2000"
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                dependentOn: 'o4_waste_method:b',
                                id_name: "o4_total_waste-b",
                                label: "Total amount of waste disposed",
                                description: "Including but not limited to metal, wood, paper/cardboard, plastics and food waste.",
                                units: "kg",
                                options: [
                                    {
                                        value: "0-500"
                                    },
                                    {
                                        value: "500-1000"
                                    },
                                    {
                                        value: "1000-2000"
                                    },
                                    {
                                        value: "2000-3000"
                                    },
                                    {
                                        value: "3000-4000"
                                    },
                                    {
                                        value: "4000-5000"
                                    },
                                    {
                                        value: "5000-6000"
                                    },
                                    {
                                        value: "6000-7000"
                                    },
                                    {
                                        value: "7000-8000"
                                    },
                                ],
                            }
                        ]
                    },
                ]
            }
        ]
    },
    "transport" : {
        tabTitle: "Transportation",
        tabDescription: "",
        sections: [
            {
                title: "Flights",
                icon: <Flight />,
                reasoning: intlReasoning,
                tag: "t-1",
                subsections: [
                    {
                        title: "International Travel ( 1 / 2 )",
                        fields: [
                            {
                                id_name: "t1_intl_method",
                                label: "How would you prefer to estimate international travel-related emissions?",
                                default_val : 'a',
                                isButtonSelect: true,
                                options: [
                                    {
                                        value: 'a',
                                        label: "Provide proportion of flight classes",
                                    },
                                    {
                                        value: 'b',
                                        label: "Provide total carbon footprint of international travel",
                                    }
                                ]
                            },
                            {
                                dependentOn: 't1_intl_method:a',
                                id_name: "t1_flight_class_proportion-a",
                                label: "Flight Class Proportion",
                                caption: "Percentage of trips booked under each flight class, taken by international attendees.",
                                description: "ICAO carbon footprint assumes that premium-class flights incur twice as much carbon emissions as their economy-class equivalents do.",
                                units: "%",
                                isSumLimit: true,
                                sumLimit: 100,
                                counterId: "frac_tripsRs",
                                options: [
                                    {
                                        id_name: "t1_fraction_economy_class-a",
                                        label: "Economy",
                                    },
                                    {
                                        id_name: "t1_fraction_premium_class-a",
                                        label: "Premium",
                                    },
                                    {
                                        id_name: "t1_fraction_private_jet-a",
                                        label: "Private Jet",
                                    },
                                ],
                            },
                            {
                                dependentOn: 't1_intl_method:b',
                                id_name: "t1_air_emission-b",
                                label: "Total International Travel Emissions",
                                description: "Input the total carbon footprint from air travel in kg CO\u2082e. This estimation is often provided by airlines. Please consider one-way/two-way trips and cabin classes and aggregate the carbon emissions from air trips of all international attendees.",
                                units: "kg CO\u2082e",
                            }
                        ]
                    },
                ],
            },
            {
                title: "Flights",
                reasoning: intlReasoning,
                tag: "t-2",
                subsections: [
                    {
                        title: "International Travel ( 2 / 2 )",
                        fields: [
                            {
                                dependentOn: 't1_intl_method:a',
                                id_name: "t1_travellers_proportion-a",
                                label: "Number of international attendees traveling from each region",
                                description: "",
                                units: "pax",
                                isSumLimit: true,
                                isInteger: true,
                                sumLimit: "global;g1_international_attendees", // To be parsed in LimitSumField component
                                counterId: "travellerRs",
                                options: [
                                    {
                                        id_name: "t1_travellers_southeast_asia-a",
                                        label: "Southeast Asia",
                                        caption: "Including Indonesia, Malaysia, Philippines, Thailand, Vietnam, etc.",
                                    },
                                    {
                                        id_name: "t1_travellers_greater_china-a",
                                        label: "East Asia",
                                        caption: "Including China, Hong Kong SAR, Taiwan, etc.",
                                    },
                                    {
                                        id_name: "t1_travellers_north_asia-a",
                                        label: "North Asia",
                                        caption: "Including Japan, South Korea, etc.",
                                    },
                                    {
                                        id_name: "t1_travellers_south_asia-a",
                                        label: "South Asia",
                                        caption: "Including India, Bangladesh, etc.",
                                    },
                                    {
                                        id_name: "t1_travellers_west_asia-a",
                                        label: "West Asia",
                                        caption: "Including UAE, Saudi Arabia, Isreal, etc.",
                                    },
                                    {
                                        id_name: "t1_travellers_americas-a",
                                        label: "Americas",
                                        caption: "Including US, Canada, etc.",
                                    },
                                    {
                                        id_name: "t1_travellers_europe-a",
                                        label: "Europe",
                                        caption: "Including United Kingdom, Germany, France, Russia, Switzerland, etc.",
                                    },
                                    {
                                        id_name: "t1_travellers_oceania-a",
                                        label: "Oceania",
                                        caption: "Including Australia, New Zealand, etc.",
                                    },
                                    {
                                        id_name: "t1_travellers_africa-a",
                                        label: "Africa",
                                        caption: "Including South Africa, Egypt, Mauritius, etc.",
                                    },
                                    {
                                        id_name: "t1_travellers_others-a",
                                        label: "Other regions",
                                        caption: "Travellers from any region NOT listed above",
                                    },
                                ],
                            },
                        ]
                    }
                ],
            },
            {
                title: "Local",
                icon: <DirectionsBus />,
                reasoning: localReasoning,
                tag: "t-3",
                subsections: [
                    {
                        title: "Local Transportation",
                        fields: [
                            {
                                id_name: "t2_local_method",
                                label: "How would you prefer to estimate local transport-related emissions?",
                                default_val : 'a',
                                isButtonSelect: true,
                                options: [
                                    {
                                        value: 'a',
                                        label: "Choose a region in Singapore",
                                    },
                                    {
                                        value: 'b',
                                        label: "Provide name of hotel accommodation",
                                    }
                                ]
                            },
                            {
                                dependentOn: 't2_local_method:a',
                                id_name: "t2_local_residence_location-a",
                                label: "Location of accomodation for international attendees",
                                caption: "Indicate the primary area where international attendees are staying during the event.",
                                description: "Select the district in Singapore for accommodations of international attendees from the dropdown menu. Carbon footprint from local transport during the event period depends on the distance of land travel from the local residence of physical attendees to the event venue. As the event venue is often chosen in the central district, the choice of residential area for international attendees largely affects the carbon emissions from local travel. Event organisers may choose one or a few close locations to accommodate the international attendees.",
                                isRadio: true,
                                isText: true,
                                options: [
                                    {
                                        value: "Central",
                                        label: "Central (e.g. Marina Bay, Orchard, Bugis)",
                                    },
                                    {
                                        value: "East",
                                        label: "East (e.g. Changi, Katong, Tampines)",
                                    },
                                    {
                                        value: "Northeast",
                                        label: "Northeast (e.g. Serangoon, Hougang, Punggol)",
                                    },
                                    {
                                        value: "North",
                                        label: "North (e.g. Woodlands, Yishun, Sembawang)",
                                    },
                                    {
                                        value: "West",
                                        label: "West (e.g. Jurong, Clementi, Bukit Batok)",
                                    },
                                ],
                            },
                            {
                                dependentOn: 't2_local_method:b',
                                id_name: "t2_local_residence_name-b",
                                label: "Name of accomodation for international attendees",
                                caption: "",
                                description: "",
                                isText: true,
                                isAddress: true,
                                adrType: [ "hotel", "lodging" ]
                            },
                        ]
                    }
                ]
            },
            {
                title: "Freight",
                icon: <DirectionsBoat />,
                reasoning: freightReasoning,
                tag: "t-4",
                subsections: [
                    {
                        title: "Freight Transportation",
                        fields: [
                            {
                                id_name: "t3_freight_mass_by_air",
                                label: "Mass transported by air",
                                caption: "Mass of goods transported to event by air freight",
                                units: "kg",
                                options: [
                                    {
                                        value: "50-100"
                                    },
                                    {
                                        value: "100-300"
                                    },
                                    {
                                        value: "300-500"
                                    },
                                    {
                                        value: "500-800"
                                    },
                                    {
                                        value: "800-1000"
                                    },
                                ],
                            },
                            {
                                id_name: "t3_freight_mass_by_sea",
                                label: "Mass transported by sea",
                                caption: "Mass of goods transported to event by sea shipping",
                                units: "kg",
                                options: [
                                    {
                                        value: "50-100"
                                    },
                                    {
                                        value: "100-300"
                                    },
                                    {
                                        value: "300-500"
                                    },
                                    {
                                        value: "500-800"
                                    },
                                    {
                                        value: "800-1000"
                                    },
                                ],
                            },
                            {
                                id_name: "t3_freight_mass_by_road",
                                label: "Mass transported by road",
                                caption: "Mass of goods transported to event by local road transport",
                                units: "kg",
                                options: [
                                    {
                                        value: "50-100"
                                    },
                                    {
                                        value: "100-300"
                                    },
                                    {
                                        value: "300-500"
                                    },
                                    {
                                        value: "500-800"
                                    },
                                    {
                                        value: "800-1000"
                                    },
                                ],
                            },
                            
                        ]
                    }
                ]
            }
        ]
    },
    "accommodations": {
        tabTitle: "Accommodations",
        tabDescription: "",
        sections: [
            {
                title: "Hotel Accommodations",
                icon: <Hotel />,
                reasoning: hotelReasoning,
                tag: "h-1",
                subsections: [
                    {
                        title: "Hotel Accommodations",
                        fields: [
                            {
                                id_name: "h1_hotel_nights",
                                label: "Number of hotel accommodation nights",
                                caption: "The number of nights booked for international attendees' hotel stay.",
                                description: "Input the hotel accommodation provided in nights. This number will affect the carbon footprint from hotel accommodation only. The accommodation period should match the number of event days in a typical accommodation plan.",
                                units: "nights",
                                options: [
                                    {
                                        value: "1"
                                    },
                                    {
                                        value: "2"
                                    },
                                    {
                                        value: "3"
                                    },
                                    {
                                        value: "4"
                                    },
                                    {
                                        value: "5"
                                    },
                                    {
                                        value: "6"
                                    },
                                    {
                                        value: "7"
                                    },
                                ],
                            },
                            {
                                id_name: "h1_room_method",
                                label: "How would you prefer to estimate hotel-related emissions?",
                                default_val : 'a',
                                isButtonSelect: true,
                                options: [
                                    {
                                        value: 'a',
                                        label: "Provide number of each room type",
                                    },
                                    {
                                        value: 'b',
                                        label: "Provide proportion of room types",
                                    }
                                ]
                            },
                            {
                                dependentOn: 'h1_room_method:a',
                                id_name: "h1_num_single_room-a",
                                label: "Number of single rooms",
                                units: "room(s)",
                            },
                            {
                                dependentOn: 'h1_room_method:a',
                                id_name: "h1_num_double_room-a",
                                label: "Number of double rooms",
                                units: "room(s)",
                            },
                            {
                                dependentOn: 'h1_room_method:b',
                                id_name: "h1_room_capacity_proportion-b",
                                label: "Room Occupancy Proportion",
                                caption: "The percentage of single-occupancy and double-occupancy hotel rooms reserved for international attendees.",
                                description: "",
                                units: "%",
                                isSumLimit: true,
                                sumLimit: 100,
                                counterId: "frac_roomOccupancyRs",
                                options: [
                                    {
                                        id_name: "h1_frac_single_room-b",
                                        label: "Single rooms",
                                    },
                                    {
                                        id_name: "h1_frac_double_room-b",
                                        label: "Double rooms",
                                    },
                                ],
                            },
                        ]
                    },
                ]
            },
        ]
    },
};